import { EFinancingProgram, getEJobTypeSelectableList } from '@if/core/types'
import {
  ControlledNumberField,
  ControlledPhoneField,
  ControlledSelect,
  ControlledTextField,
  FieldGrid,
  useTranslatedOptionEnum,
} from '@if/mui'
import { Stack } from '@mui/material'
import { useLengthInMonths, useYearOptions } from '@src/containers/ViewCreditApplicationPage/credit-hooks'
import { programConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { ApplicantJob } from '@src/types/CreditApplicationSchema'
import { Control, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import AddressComponent from './AddressComponent'

interface Props {
  control: Control<ApplicantJob>
  financingProgramId: EFinancingProgram
}

/** The job fields shared by the add/edit job dialog (see JobDialog / useFormDialog). */
const JobDialogFields = ({ control, financingProgramId }: Props) => {
  const { t } = useTranslation()
  const { setValue } = useFormContext<ApplicantJob>()
  const programConfig = programConfigByFinancingProgramId(financingProgramId)
  const jobTypeOptions = useTranslatedOptionEnum('EJobType', getEJobTypeSelectableList(), 'enum')
  const yearsOptions = useYearOptions(15)
  const { isLessThanOneYear, monthOptions, monthsName, handleYearsChange } = useLengthInMonths(control, setValue)

  return (
    <Stack spacing={3}>
      <FieldGrid columns={{ xs: 1, sm: 2 }}>
        <ControlledNumberField
          name="annualSalary"
          control={control}
          label={t('editCreditApplication.income.job.annualSalary')}
          id="annualSalary"
          isMoney
        />
        <ControlledSelect
          name="jobType"
          control={control}
          label={t('editCreditApplication.income.job.jobType')}
          options={jobTypeOptions}
          disableEmptyOption
        />
        <ControlledTextField
          id="jobTitle"
          name="jobTitle"
          control={control}
          slotProps={{ htmlInput: { maxLength: 50 } }}
          label={t('editCreditApplication.income.job.jobTitle')}
        />
        <ControlledTextField
          id="employerName"
          name="employerName"
          control={control}
          slotProps={{ htmlInput: { maxLength: 100 } }}
          label={t('editCreditApplication.income.job.employerName')}
        />
        <ControlledPhoneField
          id="employerPhone"
          name="employerPhone"
          control={control}
          label={t('editCreditApplication.income.job.employerPhone')}
        />
        <ControlledTextField
          name="employerPhoneExt"
          control={control}
          id="employerPhoneExt"
          slotProps={{ input: { inputMode: 'numeric' }, htmlInput: { maxLength: 10 } }}
          label={t('editCreditApplication.income.job.employerPhoneExt')}
        />
        <ControlledSelect
          id="jobSinceYears"
          name="years"
          control={control}
          label={t('editCreditApplication.income.job.yearsAtThisJob')}
          options={yearsOptions}
          onChange={handleYearsChange}
        />
        {isLessThanOneYear && (
          <ControlledSelect
            id="jobSinceMonths"
            name={monthsName}
            control={control}
            label={t('editCreditApplication.income.job.since.months')}
            options={monthOptions}
          />
        )}
      </FieldGrid>
      {programConfig.useJobAddress && (
        <AddressComponent formControl={control} isCommercial name="address" includeDuration={false} />
      )}
    </Stack>
  )
}

export default JobDialogFields
